import { lazy } from "react";
import type { RouteObject } from "react-router-dom";

const LayoutTenant = lazy(
  () => import("~/pages/providerWrapped/private/hasSidebar/tenant/LayoutTenant")
);
const LayoutTenantAutopay = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/LayoutTenantAutopay")
);
const LayoutTenantLeaseId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/withHeader/LayoutTenantLeaseId")
);
const LayoutTenantMaintenanceListing = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/maintenance/LayoutMaintenanceListing")
);
const LayoutTenantMaintenanceRequestId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/maintenance/LayoutMaintenanceRequestId")
);

// Template components
const MaintenanceListingTemplate = lazy(
  () => import("~/templates/maintanceRequest/MaintenanceListingTemplate")
);

// Lazy load tenant pages
const PageTenantInvitations = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/PageTenantInvitations")
);
const PageTenantPaymentMethods = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/PageTenantPaymentMethods")
);

// Leases
const PageTenantAutopay = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/PageTenantAutopay")
);
const PageTenantConditionReportId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/PageTenantConditionReportId")
);
const PageTenantLeaseId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/withHeader/PageTenantLeaseId")
);
const PageTenantLeaseIdConditionReport = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/withHeader/PageTenantLeaseIdConditionReport")
);
const PageTenantLeaseIdFiles = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/withHeader/PageTenantLeaseIdFiles")
);
const PageTenantDocumentSign = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/withHeader/PageTenantDocumentSign")
);
const PageTenantLeaseIdFilesId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/withHeader/PageTenantLeaseIdFilesId")
);
const PageTenantLeaseIdMaintenance = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/withHeader/PageTenantLeaseIdMaintenance")
);
const PageTenantLeaseIdPropertyInfo = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/withHeader/PageTenantLeaseIdPropertyInfo")
);
const PageTenantLeaseIdSecurityDeposit = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/leaseId/withHeader/PageTenantLeaseIdSecurityDeposit")
);
const PageTenantLeases = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/leases/PageTenantLeases")
);

// Documents (top-level)
const PageTenantDocuments = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/documents/PageTenantDocuments")
);
const PageTenantDocumentId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/documents/PageTenantDocumentId")
);
const PageTenantDocumentIdSign = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/documents/PageTenantDocumentIdSign")
);

// Maintenance Requests
const PageTenantMaintenanceRequestId = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/maintenance/PageMaintenanceRequestId")
);
const PageTenantMaintenanceRequestIdComments = lazy(
  () =>
    import("~/pages/providerWrapped/private/hasSidebar/tenant/maintenance/PageMaintenanceRequestIdComments")
);

export const tenantRoutes: RouteObject = {
  Component: LayoutTenant,
  path: "tenant",
  children: [
    {
      path: "invitations",
      Component: PageTenantInvitations,
    },
    {
      path: "documents",
      Component: PageTenantDocuments,
    },
    {
      path: "documents/:documentId/sign",
      Component: PageTenantDocumentIdSign,
    },
    {
      path: "documents/:documentId",
      Component: PageTenantDocumentId,
    },
    {
      path: "leases/:leaseId/files/:fileId/sign",
      Component: PageTenantDocumentSign,
    },
    {
      path: "leases/:leaseId/files/:fileId",
      Component: PageTenantLeaseIdFilesId,
    },
    {
      path: "leases/:leaseId/auto-pay",
      Component: LayoutTenantAutopay,
      children: [
        {
          index: true,
          Component: PageTenantAutopay,
        },
      ],
    },
    {
      path: "leases/:leaseId/condition-reports/:conditionReportId",
      Component: PageTenantConditionReportId,
    },
    {
      path: "leases/:leaseId",
      Component: LayoutTenantLeaseId,
      children: [
        {
          index: true,
          Component: PageTenantLeaseId,
        },
        {
          path: "condition-reports",
          Component: PageTenantLeaseIdConditionReport,
        },
        {
          path: "files",
          Component: PageTenantLeaseIdFiles,
        },
        {
          path: "maintenance",
          Component: PageTenantLeaseIdMaintenance,
        },
        {
          path: "property-info",
          Component: PageTenantLeaseIdPropertyInfo,
        },
        {
          path: "security-deposit",
          Component: PageTenantLeaseIdSecurityDeposit,
        },
      ],
    },
    {
      path: "leases",
      Component: PageTenantLeases,
    },
    {
      path: "maintenance",
      Component: LayoutTenantMaintenanceListing,
      children: [
        {
          index: true,
          element: <MaintenanceListingTemplate />,
        },
      ],
    },
    {
      path: "maintenance/:requestId",
      Component: LayoutTenantMaintenanceRequestId,
      children: [
        {
          index: true,
          element: <PageTenantMaintenanceRequestId />,
        },
        {
          path: "comments",
          Component: PageTenantMaintenanceRequestIdComments,
        },
      ],
    },
    {
      path: "payment-methods",
      Component: PageTenantPaymentMethods,
    },
  ],
};
